import { animate, style, transition, trigger } from '@angular/animations';
import { Component, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import { TextInputComponent } from '@examdojo/ui/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, debounceTime, distinctUntilChanged, filter, merge, tap } from 'rxjs';

@Component({
  selector: 'dojo-popover-content',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    TextInputComponent,
    TranslocoPipe,
    RouterLink,
    TiptapEditorWrapperComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './popover-content.component.html',
  styleUrls: ['./popover-content.component.scss'],
  animations: [
    trigger('panelAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('100ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' }))]),
    ]),
  ],
  host: {
    '[@panelAnimation]': 'true',
    class: 'block',
  },
})
export class PopoverContentComponent {
  constructor() {
    merge(this.syncInputs(), this.syncChanges()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly htmlContent = input.required<string>();
  readonly linkUrl = input<string>();
  readonly isPinned = input<boolean>(false);
  readonly allowEditing = input<boolean>(false);

  readonly isEditing = signal(false);
  readonly editorControl = new FormControl<string>('', { nonNullable: true });
  readonly linkUrlControl = new FormControl<string>('', { nonNullable: true });

  readonly closePopover = output<void>();
  readonly contentChanged = output<string>();
  readonly linkUrlChanged = output<string>();

  private readonly htmlContent$ = toObservable(this.htmlContent);
  private readonly linkUrl$ = toObservable(this.linkUrl);
  private readonly isPinned$ = toObservable(this.isPinned);
  private readonly allowEditing$ = toObservable(this.allowEditing);

  onClose(): void {
    this.closePopover.emit();
  }

  private syncInputs() {
    const syncHtmlContent$ = this.htmlContent$.pipe(
      filter(() => !this.isEditing()),
      tap((content) => this.editorControl.setValue(content, { emitEvent: false })),
    );

    const syncLinkUrl$ = this.linkUrl$.pipe(
      filter(() => !this.isEditing()),
      tap((url) => this.linkUrlControl.setValue(url ?? '', { emitEvent: false })),
    );

    const syncEditingState$ = combineLatest([this.isPinned$, this.allowEditing$]).pipe(
      tap(([isPinned, allowEditing]) => this.isEditing.set(isPinned && allowEditing)),
    );

    return merge(syncHtmlContent$, syncLinkUrl$, syncEditingState$);
  }

  private syncChanges() {
    const contentChanges$ = this.editorControl.valueChanges.pipe(
      filter(() => this.isEditing()),
      debounceTime(300),
      distinctUntilChanged(),
      tap((value) => {
        this.contentChanged.emit(value);
      }),
    );

    const linkUrlChanges$ = this.linkUrlControl.valueChanges.pipe(
      filter(() => this.isEditing()),
      debounceTime(300),
      distinctUntilChanged(),
      tap((value) => {
        this.linkUrlChanged.emit(value);
      }),
    );

    return merge(contentChanges$, linkUrlChanges$);
  }
}
